import { useEffect, useState } from 'react';
import Icon from '../components/Icon';
import PageHeader from '../components/PageHeader';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ApiError } from '../lib/api';
import {
  getConversationRequest,
  listConversationsRequest,
  listMessagesRequest,
  pauseConversationRequest,
  resumeConversationRequest,
  type ConversationInboxItem,
  type ConversationMessageItem,
} from '../lib/conversations-api';

type ListStatus = 'loading' | 'ready' | 'error';
type ThreadStatus = 'idle' | 'loading' | 'ready' | 'error' | 'not_found';
type MobileView = 'list' | 'thread';
type ConfirmKind = 'pause' | 'resume';

const CONVERSATION_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 20;

function formatTimestamp(timestamp: number | null): string {
  if (timestamp === null) {
    return '—';
  }
  return new Date(timestamp).toLocaleString('fa-IR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function conversationName(conversation: ConversationInboxItem): string {
  if (conversation.username !== null && conversation.username !== '') {
    return conversation.username;
  }
  return conversation.igsid;
}

function messageBody(message: ConversationMessageItem): string {
  if (message.text !== null && message.text !== '') {
    return message.text;
  }
  if (message.kind === 'story_reply') {
    return 'پاسخ به استوری';
  }
  if (message.kind === 'media') {
    return 'رسانه';
  }
  if (message.kind === 'mention') {
    return 'اشاره';
  }
  return 'پیام بدون متن';
}

function serverErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status >= 500) {
    return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
  }
  return '';
}

function listErrorMessage(error: unknown): string {
  return serverErrorMessage(error) || 'دریافت فهرست مکالمات ناموفق بود. دوباره تلاش کنید.';
}

function threadErrorMessage(error: unknown): string {
  return serverErrorMessage(error) || 'دریافت پیام‌ها ناموفق بود. دوباره تلاش کنید.';
}

function pauseResumeErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'برای انجام این عملیات دوباره وارد حساب خود شوید.';
    }
    if (error.status === 404) {
      return 'این مکالمه یافت نشد یا به حساب دیگری تعلق دارد.';
    }
    if (error.status >= 500) {
      return 'خطای سرور رخ داد. کمی بعد دوباره تلاش کنید.';
    }
    return 'انجام عملیات ناموفق بود. دوباره تلاش کنید.';
  }
  if (error instanceof TypeError) {
    return 'ارتباط با سرور برقرار نشد. دوباره تلاش کنید.';
  }
  return 'انجام عملیات ناموفق بود. دوباره تلاش کنید.';
}

export default function InboxPage() {
  useDocumentTitle('صندوق ورودی');

  const [listStatus, setListStatus] = useState<ListStatus>('loading');
  const [conversations, setConversations] = useState<ConversationInboxItem[]>([]);
  const [listTotal, setListTotal] = useState(0);
  const [listError, setListError] = useState<string | null>(null);
  const [listReloadKey, setListReloadKey] = useState(0);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [conversationsMoreError, setConversationsMoreError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<MobileView>('list');
  const [threadStatus, setThreadStatus] = useState<ThreadStatus>('idle');
  const [threadReloadKey, setThreadReloadKey] = useState(0);
  const [detail, setDetail] = useState<ConversationInboxItem | null>(null);
  const [messagePages, setMessagePages] = useState<ConversationMessageItem[][]>([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [messagesMoreError, setMessagesMoreError] = useState<string | null>(null);

  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listConversationsRequest(CONVERSATION_PAGE_SIZE, 0)
      .then((response) => {
        if (!cancelled) {
          setConversations(response.conversations);
          setListTotal(response.total);
          setListError(null);
          setListStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setListError(listErrorMessage(error));
          setListStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [listReloadKey]);

  useEffect(() => {
    if (selectedId === null) {
      return;
    }
    let cancelled = false;
    const conversationId = selectedId;
    Promise.all([
      getConversationRequest(conversationId),
      listMessagesRequest(conversationId, MESSAGE_PAGE_SIZE, 0),
    ])
      .then(([detailResponse, messagesResponse]) => {
        if (!cancelled) {
          setDetail(detailResponse.conversation);
          setMessagePages([messagesResponse.messages]);
          setMessagesTotal(messagesResponse.total);
          setThreadError(null);
          setMessagesMoreError(null);
          setThreadStatus('ready');
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setThreadError(threadErrorMessage(error));
          setThreadStatus(
            error instanceof ApiError && error.status === 404 ? 'not_found' : 'error',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, threadReloadKey]);

  function handleRetryList(): void {
    setListError(null);
    setListStatus('loading');
    setListReloadKey((key) => key + 1);
  }

  function handleLoadMoreConversations(): void {
    setLoadingMoreConversations(true);
    setConversationsMoreError(null);
    listConversationsRequest(CONVERSATION_PAGE_SIZE, conversations.length)
      .then((response) => {
        setConversations((current) => [...current, ...response.conversations]);
        setListTotal(response.total);
      })
      .catch(() => {
        setConversationsMoreError('دریافت مکالمات بیشتر ناموفق بود. دوباره تلاش کنید.');
      })
      .finally(() => {
        setLoadingMoreConversations(false);
      });
  }

  function handleSelect(conversation: ConversationInboxItem): void {
    if (conversation.id === selectedId) {
      setMobileView('thread');
      return;
    }
    setSelectedId(conversation.id);
    setDetail(null);
    setMessagePages([]);
    setMessagesTotal(0);
    setThreadError(null);
    setMessagesMoreError(null);
    setThreadStatus('loading');
    setConfirmKind(null);
    setActionError(null);
    setMobileView('thread');
  }

  function handleRetryThread(): void {
    setThreadError(null);
    setThreadStatus('loading');
    setThreadReloadKey((key) => key + 1);
  }

  function handleLoadMoreMessages(): void {
    if (selectedId === null) {
      return;
    }
    const offset = messagePages.length * MESSAGE_PAGE_SIZE;
    setLoadingMoreMessages(true);
    setMessagesMoreError(null);
    listMessagesRequest(selectedId, MESSAGE_PAGE_SIZE, offset)
      .then((response) => {
        setMessagePages((pages) => [...pages, response.messages]);
        setMessagesTotal(response.total);
      })
      .catch(() => {
        setMessagesMoreError('دریافت پیام‌های بیشتر ناموفق بود. دوباره تلاش کنید.');
      })
      .finally(() => {
        setLoadingMoreMessages(false);
      });
  }

  function handleActionClick(): void {
    if (selectedConversation === null || actionPending || confirmKind !== null) {
      return;
    }
    setActionError(null);
    setConfirmKind(selectedConversation.automation_paused ? 'resume' : 'pause');
  }

  function handleCancelConfirm(): void {
    if (actionPending) {
      return;
    }
    setConfirmKind(null);
    setActionError(null);
  }

  function handleConfirmAction(): void {
    if (selectedConversation === null || confirmKind === null || actionPending) {
      return;
    }
    const kind = confirmKind;
    const targetId = selectedId;
    const target = selectedConversation;
    setActionPending(true);
    setActionError(null);
    const request =
      kind === 'pause'
        ? pauseConversationRequest(target.account_id, target.igsid)
        : resumeConversationRequest(target.account_id, target.igsid);
    request
      .then((response) => {
        const patch = {
          automation_paused: response.automation_paused,
          paused_at: response.paused_at,
        };
        setDetail((current) =>
          current !== null && current.id === targetId ? { ...current, ...patch } : current,
        );
        setConversations((current) =>
          current.map((item) => (item.id === targetId ? { ...item, ...patch } : item)),
        );
        setConfirmKind(null);
        setActionError(null);
      })
      .catch((error: unknown) => {
        setActionError(pauseResumeErrorMessage(error));
      })
      .finally(() => {
        setActionPending(false);
      });
  }

  const selectedConversation =
    detail ??
    (selectedId !== null ? (conversations.find((item) => item.id === selectedId) ?? null) : null);
  const visibleMessages = messagePages.flat().reverse();
  const hasMoreConversations = listStatus === 'ready' && conversations.length < listTotal;
  const hasMoreMessages = threadStatus === 'ready' && visibleMessages.length < messagesTotal;

  return (
    <div className="space-y-6">
      <PageHeader
        title="صندوق ورودی"
        description="مکالمات و پیام‌های خصوصی دریافت‌شده از اینستاگرام"
      />

      <div className="flex h-[calc(100vh-15rem)] min-h-[26rem] flex-row-reverse overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <aside
          data-testid="conversation-list"
          className={`${mobileView === 'list' ? 'flex' : 'hidden'} w-full flex-col border-slate-200 lg:flex lg:w-80 lg:shrink-0 lg:border-r`}
        >
          {listStatus === 'loading' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
              <p className="text-sm text-slate-500">در حال دریافت مکالمات…</p>
            </div>
          )}

          {listStatus === 'error' && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <p className="text-sm text-rose-700">{listError}</p>
              <button
                type="button"
                onClick={handleRetryList}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                تلاش مجدد
              </button>
            </div>
          )}

          {listStatus === 'ready' && conversations.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-400">
                <Icon name="inbox" className="h-6 w-6" />
              </span>
              <h3 className="text-base font-bold text-slate-800">هنوز مکالمه‌ای وجود ندارد</h3>
              <p className="text-sm leading-6 text-slate-500">
                پیام‌های خصوصی دریافت‌شده از اینستاگرام پس از ثبت رویدادها، اینجا نمایش داده
                می‌شوند.
              </p>
            </div>
          )}

          {listStatus === 'ready' && conversations.length > 0 && (
            <div className="flex-1 overflow-y-auto">
              {conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  onClick={() => handleSelect(conversation)}
                  aria-current={conversation.id === selectedId ? 'true' : undefined}
                  className={`flex w-full flex-col gap-1.5 border-b border-slate-100 px-4 py-3 text-right transition-colors ${
                    conversation.id === selectedId ? 'bg-indigo-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex w-full items-center gap-3">
                    <span
                      aria-hidden="true"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-sm font-bold text-indigo-400"
                    >
                      {(conversation.username ?? conversation.igsid).slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          dir="ltr"
                          className="truncate text-left text-sm font-bold text-slate-900"
                        >
                          {conversationName(conversation)}
                        </span>
                        <span className="shrink-0 text-xs text-slate-400">
                          {formatTimestamp(conversation.last_message_at ?? conversation.updated_at)}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center justify-between gap-2">
                        <p className="truncate text-sm text-slate-500">
                          {conversation.last_message_preview ?? 'بدون پیام'}
                        </p>
                        <span className="flex shrink-0 items-center gap-1.5">
                          {conversation.automation_paused && (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                              اتومیشن متوقف
                            </span>
                          )}
                          {conversation.unread && (
                            <span
                              data-testid="unread-indicator"
                              aria-label="خوانده‌نشده"
                              className="h-2.5 w-2.5 rounded-full bg-indigo-600"
                            />
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {(hasMoreConversations || conversationsMoreError !== null) && (
            <div className="border-t border-slate-100 p-3">
              {hasMoreConversations && (
                <button
                  type="button"
                  onClick={handleLoadMoreConversations}
                  disabled={loadingMoreConversations}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingMoreConversations ? 'در حال بارگذاری…' : 'نمایش مکالمات بیشتر'}
                </button>
              )}
              {conversationsMoreError !== null && (
                <p role="alert" className="mt-2 text-center text-xs text-rose-700">
                  {conversationsMoreError}
                </p>
              )}
            </div>
          )}
        </aside>

        <section
          data-testid="thread-panel"
          className={`${mobileView === 'thread' ? 'flex' : 'hidden'} min-w-0 flex-1 flex-col lg:flex`}
        >
          {selectedId === null ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-400">
                <Icon name="inbox" className="h-6 w-6" />
              </span>
              <h3 className="text-base font-bold text-slate-800">یک مکالمه را انتخاب کنید</h3>
              <p className="max-w-md text-sm leading-6 text-slate-500">
                برای مشاهده پیام‌ها، یکی از مکالمات فهرست کناری را انتخاب کنید.
              </p>
            </div>
          ) : (
            <>
              <header className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setMobileView('list')}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 lg:hidden"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                  بازگشت به فهرست
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p dir="ltr" className="truncate text-left text-sm font-bold text-slate-900">
                      {selectedConversation !== null
                        ? conversationName(selectedConversation)
                        : 'مکالمه'}
                    </p>
                    {selectedConversation !== null && (
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                          selectedConversation.automation_paused
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {selectedConversation.automation_paused ? 'اتومیشن متوقف' : 'اتومیشن فعال'}
                      </span>
                    )}
                  </div>
                </div>
                {threadStatus === 'ready' && selectedConversation !== null && (
                  <button
                    type="button"
                    onClick={handleActionClick}
                    disabled={actionPending || confirmKind !== null}
                    className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      selectedConversation.automation_paused
                        ? 'bg-emerald-600 text-white hover:bg-emerald-800'
                        : 'border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                    }`}
                    title="فقط برای همین مکالمه"
                  >
                    {actionPending
                      ? 'در حال پردازش…'
                      : selectedConversation.automation_paused
                        ? 'فعال‌سازی خودکار'
                        : 'توقف خودکار'}
                  </button>
                )}
              </header>

              {confirmKind !== null && (
                <div
                  role="alertdialog"
                  aria-label="تایید عملیات اتومیشن"
                  className="border-b border-amber-200 bg-amber-50 px-4 py-3"
                >
                  <p data-testid="confirm-message" className="text-sm leading-6 text-amber-900">
                    {confirmKind === 'pause'
                      ? 'توقف خودکار: پاسخ‌های خودکار برای این مکالمه ارسال نمی‌شود. آیا مطمئن هستید؟'
                      : 'فعال‌سازی خودکار: پاسخ‌های خودکار برای این مکالمه دوباره فعال می‌شود. آیا مطمئن هستید؟'}
                  </p>
                  {actionError !== null && (
                    <p role="alert" className="mt-2 text-sm font-semibold text-rose-700">
                      {actionError}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleConfirmAction}
                      disabled={actionPending}
                      className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {actionPending ? 'در حال پردازش…' : 'تایید'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelConfirm}
                      disabled={actionPending}
                      className="rounded-lg border border-amber-300 px-4 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      انصراف
                    </button>
                  </div>
                </div>
              )}

              {threadStatus === 'loading' && (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
                  <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
                  <p className="text-sm text-slate-500">در حال دریافت پیام‌ها…</p>
                </div>
              )}

              {threadStatus === 'not_found' && (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                    <Icon name="inbox" className="h-6 w-6" />
                  </span>
                  <h3 className="text-base font-bold text-slate-800">مکالمه موردنظر یافت نشد</h3>
                  <p className="max-w-md text-sm leading-6 text-slate-500">
                    این مکالمه دیگر در دسترس نیست یا به حساب دیگری تعلق دارد.
                  </p>
                </div>
              )}

              {threadStatus === 'error' && (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
                  <p className="text-sm text-rose-700">{threadError}</p>
                  <button
                    type="button"
                    onClick={handleRetryThread}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    تلاش مجدد
                  </button>
                </div>
              )}

              {threadStatus === 'ready' && (
                <div
                  data-testid="message-list"
                  className="flex flex-1 flex-col gap-3 overflow-y-auto p-4"
                >
                  {visibleMessages.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
                      <h3 className="text-sm font-bold text-slate-700">
                        هنوز پیامی در این مکالمه وجود ندارد
                      </h3>
                      <p className="max-w-md text-xs leading-6 text-slate-500">
                        پیام‌های دریافتی این مکالمه پس از ثبت رویداد، اینجا نمایش داده می‌شوند.
                      </p>
                    </div>
                  ) : (
                    visibleMessages.map((message) => (
                      <article
                        key={message.id}
                        data-testid="message-bubble"
                        data-direction={message.direction}
                        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-6 ${
                          message.direction === 'outbound'
                            ? 'self-end bg-indigo-600 text-white'
                            : 'self-start border border-slate-200 bg-white text-slate-800'
                        }`}
                      >
                        <p>{messageBody(message)}</p>
                        <time
                          dateTime={new Date(message.created_at).toISOString()}
                          className={`mt-1 block text-[11px] ${
                            message.direction === 'outbound' ? 'text-indigo-100' : 'text-slate-400'
                          }`}
                        >
                          {formatTimestamp(message.created_at)}
                        </time>
                      </article>
                    ))
                  )}
                </div>
              )}

              {threadStatus === 'ready' && (hasMoreMessages || messagesMoreError !== null) && (
                <div className="border-t border-slate-200 p-3 text-center">
                  {hasMoreMessages && (
                    <button
                      type="button"
                      onClick={handleLoadMoreMessages}
                      disabled={loadingMoreMessages}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {loadingMoreMessages ? 'در حال بارگذاری…' : 'نمایش پیام‌های بیشتر'}
                    </button>
                  )}
                  {messagesMoreError !== null && (
                    <p role="alert" className="mt-2 text-xs text-rose-700">
                      {messagesMoreError}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
